import React, { FC, useMemo } from 'react'

/* mui */
import { Stack, Box, Button, useTheme } from '@mui/material'

/* icons */
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import DashboardIcon from '@mui/icons-material/Dashboard'

/* helpers */
import { getHasHighCheckPriority } from '../../../helpers/getHasHighCheckPriority'

/* component */
import { Copyright } from '../../../components/molecules/Copyright'
import { MessageOverlay } from '../../../components/molecules/MessageOverlay'
import { OCR_ERROR_MESSAGE_LEGACY } from '../../../constants/const'
import {
  MelphinPatientMatchingBasicResultWindow,
  MelphinNonMatchingBasicResultWindow,
} from '../components/organisms/MelphinBasicResult'
import { ColumnBasedMedicineResult } from '../../../components/organisms/MedicineResult'
import { QrImagesModalBody } from '../../../components/templates/QrImagesModalBody'
import { InitialScreenBody } from '../../../components/templates/InitialScreenBody'
import { CustomFab } from '../../../components/molecules/CustomFab'
import { RequestIdDisplay } from '../../../components/organisms/RequestIdDisplay'

/* hooks */
import {
  usePrescriptions,
  useSelectedPresc,
  usePrescriptionImageBox,
  useEditPresc,
  useCameraModal as useCamera,
} from '../../../features/prescription'
import {
  useModal,
  useMedicationAlert,
  medicationAlertOpenButton,
} from '../../../shared'
import { MelphinDrawerToggleButton } from '../hooks/useMelphinDrawerToggle'
import { useReportPresc } from '../hooks/useReport'
import { useNavigate } from 'react-router-dom'

const MelphinIndexPage: FC = () => {
  const navigate = useNavigate()
  const { AdditionalPrescSnackBar, prescriptions, LoadingOverlay } =
    usePrescriptions()
  const { selectedPresc: userPresc, selectOcrRequest } = useSelectedPresc({
    prescriptions,
  })

  const theme = useTheme()
  const selectedPresc = userPresc

  const { editBasicResult, updatingPrescription } = useEditPresc({
    prescription: selectedPresc,
  })

  const {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    openModal,
    renderSnackBar,
    ReportSendingOverlay,
    renderHelpContentModal,
  } = useReportPresc({
    prescription: selectedPresc,
  })

  const { renderCameraModal, OpenButton, DuringOcrOverlay } = useCamera()

  const { MedicationModal, setMedicationAlertOpen } = useMedicationAlert(
    selectedPresc?.id,
    selectedPresc?.low_accuracy_alerts,
    selectedPresc?.reshape_reports,
    selectedPresc?.gigi_alerts,
    selectedPresc?.med_change_alerts
  )

  // MediLab系では常にポータルボタンを表示

  const PortalNavigateButton = (
    <>
      <Button
        sx={{
          width: '100%',
          whiteSpace: 'pre-wrap',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
        onClick={() => navigate('/portal')}
      >
        <DashboardIcon sx={{ fontSize: 20 }} />
        {'管理ポータルへ'}
      </Button>
    </>
  )

  const drawerButtomButtons = [PortalNavigateButton]

  const prescImages = useMemo(
    () => selectedPresc?.presc_images || [],
    [selectedPresc]
  )

  const prescImagesArea = useMemo(
    () => selectedPresc?.basic_result?.処方箋存在領域?.selected_option || [],
    [selectedPresc]
  )

  const { prescImageShown, PrescImageBox, PrescImageToggleButton } =
    usePrescriptionImageBox(prescImages, prescImagesArea, {
      viewerControls: true,
    })

  const { openModal: openQrImageModal, renderModal: renderQrImageModal } =
    useModal(
      selectedPresc && <QrImagesModalBody prescription={selectedPresc} />,
      'center'
    )

  if (!selectedPresc) {
    return (
      <>
        {LoadingOverlay}
        <MelphinDrawerToggleButton
          PrescImageToggleButton={PrescImageToggleButton}
          prescriptions={prescriptions}
          selectOcrRequest={selectOcrRequest}
          BottomButtons={drawerButtomButtons}
        />
        <InitialScreenBody />
        {DuringOcrOverlay}
        {OpenButton}
        {renderCameraModal()}
      </>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.palette.secondary.light,
      }}
    >
      {DuringOcrOverlay}
      {ReportSendingOverlay}
      <MelphinDrawerToggleButton
        selectedPresc={selectedPresc}
        PrescImageToggleButton={PrescImageToggleButton}
        prescriptions={prescriptions}
        selectOcrRequest={selectOcrRequest}
        BottomButtons={drawerButtomButtons}
      />

      {PrescImageBox}
      <Box
        sx={{
          [theme.breakpoints.down('md')]: prescImageShown
            ? {
                display: 'none',
              }
            : {
                width: '100%',
              },
        }}
        width="50%"
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'scroll',
            height: '100vh',
          }}
        >
          {selectedPresc.was_error && MessageOverlay(OCR_ERROR_MESSAGE_LEGACY)}
          {selectedPresc.was_read_using_jahis_qr &&
            MessageOverlay(
              '処方箋に印字のQRから読込みました。\n\nOCR処理は行われませんでした。'
            )}
          {!selectedPresc.was_error && MedicationModal}
          {!selectedPresc.was_read_using_jahis_qr && (
            <div>
              <Stack bgcolor={'primary.main'} p={2} spacing={1}>
                {/* Melphin専用のBasicResultWindowを使用 */}
                <MelphinPatientMatchingBasicResultWindow
                  editBasicResult={editBasicResult}
                  reportBasicResult={reportBasicResult}
                  selectedPresc={selectedPresc}
                  isCheckboxDisabled={updatingPrescription}
                  openQrImageModal={openQrImageModal}
                  getHasHighCheckPriority={getHasHighCheckPriority}
                />
                <MelphinNonMatchingBasicResultWindow
                  editBasicResult={editBasicResult}
                  reportBasicResult={reportBasicResult}
                  selectedPresc={selectedPresc}
                  isCheckboxDisabled={updatingPrescription}
                  openQrImageModal={openQrImageModal}
                  getHasHighCheckPriority={getHasHighCheckPriority}
                />
              </Stack>
              <Stack px={2} pb={20}>
                {/* MediLab系では従来のColumnBasedMedicineResultを使用 */}
                <ColumnBasedMedicineResult
                  prescription={selectedPresc}
                  reportRPUnit={reportRPUnit}
                  reportMedication={reportMedication}
                />
              </Stack>
            </div>
          )}

          {medicationAlertOpenButton(() => setMedicationAlertOpen(true))}
          <CustomFab
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.primary.translucent,
                color: '#fff',
              },
              bottom: 90,
            }}
            onClick={openModal}
          >
            <HelpOutlineIcon style={{ fontSize: 35 }} />
          </CustomFab>
          {renderSnackBar()}
          {renderHelpContentModal}

          <Box p={2}>
            <RequestIdDisplay requestId={selectedPresc.ocr_request_event_id} />
            <Copyright />
          </Box>
        </div>
        {AdditionalPrescSnackBar()}
        {renderQrImageModal()}
        {OpenButton}
        {renderCameraModal()}
      </Box>
    </div>
  )
}

export default MelphinIndexPage
